import { ErrorHandler, Injectable, inject, NgZone } from '@angular/core';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
  private errorHandlerService = inject(ErrorHandlerService);
  private ngZone = inject(NgZone);

  /**
   * Handle global unhandled errors
   * This method is called whenever an error occurs that wasn't caught elsewhere
   */
  handleError(error: any): void {
    // Run outside Angular zone to prevent infinite loops
    this.ngZone.runOutsideAngular(() => {
      try {
        // Log to console first for debugging
        console.error('🚨 Global Error Caught:', error);

        // Extract meaningful information from the error
        const errorInfo = this.extractErrorInfo(error);

        // Use our error handler service to process and notify
        this.ngZone.run(() => {
          this.errorHandlerService.handleError(error, {
            component: 'GlobalErrorHandler',
            action: 'unhandled_error',
            errorType: errorInfo.type,
            stack: errorInfo.stack,
            originalMessage: errorInfo.message,
          });
        });
      } catch (handlingError) {
        // Fallback: if our error handling fails, log to console and show alert
        console.error('❌ Error in error handler:', handlingError);
        console.error('❌ Original error:', error);

        // Last resort: browser alert
        if (typeof alert !== 'undefined') {
          alert('Ocorreu um erro inesperado. Por favor, recarregue a página.');
        }
      }
    });
  }

  /**
   * Extract useful information from various error types
   */
  private extractErrorInfo(error: any): {
    type: string;
    message: string;
    stack?: string;
  } {
    // Angular error wrapper
    if (error?.rejection) {
      return this.extractErrorInfo(error.rejection);
    }

    // Promise rejection
    if (error?.promise) {
      return {
        type: 'promise_rejection',
        message: error.reason?.message || error.reason || 'Promise rejection',
        stack: error.reason?.stack,
      };
    }

    // Standard JavaScript Error
    if (error instanceof Error) {
      return {
        type: 'javascript_error',
        message: error.message,
        stack: error.stack,
      };
    }

    // HTTP Error Response
    if (error?.status !== undefined) {
      return {
        type: 'http_error',
        message: error.message || `HTTP ${error.status}`,
        stack: error.stack,
      };
    }

    // String error
    if (typeof error === 'string') {
      return {
        type: 'string_error',
        message: error,
      };
    }

    // Object with message
    if (error?.message) {
      return {
        type: 'object_error',
        message: error.message,
        stack: error.stack,
      };
    }

    // Unknown error type
    return {
      type: 'unknown_error',
      message: 'Erro inesperado ocorreu',
      stack: JSON.stringify(error, null, 2),
    };
  }
}
